import { Link, Tooltip as TooltipMUI } from "@mui/material";
import {
  GridActionsCellItem,
  GridCallbackDetails,
  GridRowParams,
  GridSortModel
} from "@mui/x-data-grid-pro";
import { DateLabel } from "klayowebshared";
import React, { ReactNode, SyntheticEvent } from "react";
import { Utils } from "../../common/Utils";
import { AbstractAttribute } from "../../data/attributes/AbstractAttribute";
import AttachmentIcon from "../../resources/images/icons-attachment.svg";
import CertificateIcon from "../../resources/images/icons-certificate.svg";
import CheckIcon from "../../resources/images/icons-check.svg";
import DeleteIcon from "../../resources/images/icons-delete.svg";
import NeedsFeedbackIcon from "../../resources/images/icons-feedback.svg";
import MoreVertIcon from "../../resources/images/icons-more-vertical.svg";
import StarIcon from "../../resources/images/icons-star.svg";
import WarningIcon from "../../resources/images/icons-warning.svg";
import type {
  BaseTableRow,
  TableColumnDef,
  TableProps,
  TableState,
  TreeNodeWithChildren,
  ValidationRequestState
} from "../../types/components/table.types";
import { getHeaderName } from "../../types/components/table.types";
import { Button } from "../Button";
import { ProfilePictureGroup } from "../ProfilePictureGroup";
import { SelectMenu } from "../SelectMenu";
import { Tooltip } from "../Tooltip";
import { TooltipIcon } from "../TooltipIcon";
import { Table } from "./Table";

import { Abstract_Attribute, EmployeeAttributeValidationRequestType } from "@/constants";
import { Organization } from "@/data/Organization";
import { AttributeRequirementType } from "@/data/settings/SettingsAttribute";
import { SettingsAttributeCategory } from "@/data/settings/SettingsAttributeCategory";
import { Employee } from "@/data/users/Employee";
import { RequirementType } from "@/types/models/settings.types";
import styled from "@emotion/styled";
import { isEmpty } from "lodash";
import { KLAYO_COLORS } from "../../themes";
import { highlightKeyword } from "../../utilities";
import { Chip } from "../Chip";

/** Helper to get visible from breakpoint with proper typing */
function getVisibleFromBreakpoint<T extends BaseTableRow>(
  c: TableColumnDef<T>
): string | undefined {
  return (
    (c.visibleFromBreakpoint ?? (typeof c.type === "object" && c.type?.visibleFromBreakpoint)) ||
    undefined
  );
}

/** Helper to get visible to breakpoint with proper typing */
function getVisibleToBreakpoint<T extends BaseTableRow>(c: TableColumnDef<T>): string | undefined {
  return (
    (c.visibleToBreakpoint ?? (typeof c.type === "object" && c.type?.visibleToBreakpoint)) ||
    undefined
  );
}

interface AttributeTableRow extends BaseTableRow {
  attributeHierarchy?: string[];
  currentProficiency?: number;
  requiredProficiency?: number;
  validated?: boolean;
  hasValidationRequest?: () => boolean;
  validationRequestState?: ValidationRequestState;
  gapEmployees?: Employee[];
  metEmployees?: Employee[];
  employees?: Employee[];
  numberOfJobAttributes?: number;
  numberOfEmployeeAttributes?: number;
  needsFeedback?: boolean;
  comment?: string;
  expiryDate?: Date | null;
  requestedExpiryDate?: Date | null;
  reasonLabel?: string;
  isChild?: boolean;
  requirementType?: AttributeRequirementType | number;
  attributeType?: string;
  category?: string;
  reason?: ValidationRequestState;
  lastUpdate?: string;
  summary?: string;
  summaryWithPeople?: string;
  requestTitle?: string;
  attribute?: string;
  averageProficiency?: number;
  name?: string;
  status?: (typeof AbstractAttribute.status)[keyof typeof AbstractAttribute.status];
  categories?: SettingsAttributeCategory[];
  originalValidationRequestState?: ValidationRequestState;
  type?: string;
  statusLabel?: string;
}

/** Extended props for AttributeTable */
interface AttributeTableOwnProps<
  T extends AttributeTableRow = AttributeTableRow
> extends TableProps<T> {
  showProficiency?: boolean;
  isGapMode?: boolean;
  rowHasAction?: boolean | ((row: T) => boolean);
  rowNoActionIcon?: React.ReactNode;
  rowActionDisabled?: (row: T) => boolean;
  childRowClass?: (row: T) => string;
  onAttributeSelect?: (event: SyntheticEvent, row: T) => void;
  onAttributeAction?: (event: SyntheticEvent, row: T) => void;
  onAttributeDelete?: (event: SyntheticEvent, row: T) => void;
  onAttributeEdit?: (event: SyntheticEvent, row: T) => void;
  onRequirementChange?: (row: T, requirementType: RequirementType) => void;
  onSelectGap?: (event: SyntheticEvent, row: T) => void;
  onSelectHave?: (event: SyntheticEvent, row: T) => void;
  onSelectPeople?: (event: SyntheticEvent, row: T) => void;
  organization?: Organization;
  onCloseGoToTraining?: (event: SyntheticEvent, row: T) => void;
  /** Observe sort changes for analytics without hijacking the grid's built-in client sort
   *  (unlike onSortModelChange, which replaces the internal handler). */
  onSortTracked?: (model: GridSortModel) => void;
}

/** Extended state for AttributeTable */
interface AttributeTableState extends TableState {
  isGapMode: boolean;
}

export class AttributeTable<T extends AttributeTableRow = AttributeTableRow> extends Table<
  T,
  AttributeTableOwnProps<T>,
  AttributeTableState
> {
  static columns = {
    summary: {
      field: "summary",
      label: "tableColumns.summary",
      visibleToBreakpoint: "md"
    },
    summaryWithPeople: {
      field: "summaryWithPeople",
      label: "tableColumns.summary",
      visibleToBreakpoint: "md"
    },
    lastUpdate: {
      field: "lastUpdate",
      label: "tableColumns.lastUpdate",
      visibleFromBreakpoint: undefined
    },
    attribute: {
      field: "attribute",
      label: "tableColumns.jobCompetency",
      visibleFromBreakpoint: "md",
      showJobMatchingIcon: false
    },
    requestTitle: {
      field: "requestTitle",
      label: "tableColumns.jobCompetency"
    },
    category: {
      field: "category",
      label: "tableColumns.category",
      visibleFromBreakpoint: "lg"
    },
    attributeType: {
      field: "attributeType",
      label: "tableColumns.priority",
      visibleFromBreakpoint: "md",
      selectable: false,
      selectableOnChild: true
    },
    requirementType: {
      field: "requirementType",
      label: "tableColumns.type",
      visibleFromBreakpoint: "md",
      selectable: false,
      selectableOnChild: true
    },
    currentProficiency: {
      field: "currentProficiency",
      label: "tableColumns.currentProficiency",
      visibleFromBreakpoint: "md"
    },
    requiredProficiency: {
      field: "requiredProficiency",
      label: "tableColumns.requiredProficiency",
      visibleFromBreakpoint: "md"
    },
    averageProficiency: {
      field: "averageProficiency",
      label: "tableColumns.averageProficiency",
      visibleFromBreakpoint: "md"
    },
    status: {
      field: "status",
      label: "tableColumns.status"
    },
    reason: {
      field: "reason",
      label: "tableColumns.status",
      visibleFromBreakpoint: "md"
    },
    reasonLong: {
      field: "reasonLong",
      label: "tableColumns.status",
      visibleFromBreakpoint: "md"
    },
    comment: {
      field: "comment",
      label: "tableColumns.comment"
    },
    documents: {
      field: "documents",
      label: "tableColumns.documents"
    },
    validationRequestStateTest: {
      field: "originalValidationRequestState",
      label: "tableColumns.statusTesting"
    },
    expiryTest: {
      field: "expiryTest",
      label: "tableColumns.expiryTesting"
    },
    expiry: {
      field: "expiry",
      label: "tableColumns.expiry"
    },
    employees: {
      field: "employees",
      label: "tableColumns.employees"
    },
    actions: {
      field: "actions",
      label: "tableColumns.actions",
      visibleFromBreakpoint: "md"
    },
    actionsMore: {
      field: "actions",
      label: "tableColumns.actions",
      visibleFromBreakpoint: "md"
    },
    edit: {
      field: "edit",
      label: "tableColumns.edit"
    },
    delete: {
      field: "delete",
      label: "tableColumns.delete"
    },
    gapIcon: {
      field: "gapIcon",
      label: "tableColumns.gap",
      visibleFromBreakpoint: "md"
    },
    attachmentIcon: {
      field: "attachmentIcon",
      label: "tableColumns.attachment",
      visibleFromBreakpoint: "md"
    },
    gapIconOld: {
      field: "gapIconOld",
      label: "tableColumns.gapTest"
    },
    gapIconTest: {
      field: "gapIconTest",
      label: "tableColumns.gapTest"
    },
    numEmployees: {
      field: "numEmployees",
      label: "tableColumns.employees"
    },
    numJobs: {
      field: "numJobs",
      label: "tableColumns.jobs"
    },
    needsFeedback: {
      field: "needsFeedback",
      label: ""
    },
    hasTrainingCourses: {
      field: "hasTrainingCourses",
      label: ""
    },
    regulationPeople: {
      field: "regulationPeople",
      label: "tableColumns.people"
    },
    regulationGap: {
      field: "regulationGap",
      label: "tableColumns.gap"
    },
    gapEmployees: {
      field: "gapEmployees",
      label: "tableColumns.gaps"
    },
    attributeEmployees: {
      field: "attributeEmployees",
      label: "tableColumns.people"
    },
    metEmployees: {
      field: "metEmployees",
      label: "tableColumns.have"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    columns: [AttributeTable.columns.attribute],
    noRowsMessage: "No competencies, yet",
    showProficiency: false,
    rowHasAction: null,
    disableSelection: true
  };

  constructor(props: AttributeTableOwnProps<T>) {
    super(props);

    this.state = {
      ...this.state,
      sortModel: props.sortModel
        ? props.sortModel
        : [
            {
              field: this.hasColumn(AttributeTable.columns.lastUpdate) ? "lastUpdate" : "attribute",
              sort: this.hasColumn(AttributeTable.columns.lastUpdate) ? "desc" : "asc"
            }
          ],
      isGapMode: props.isGapMode || false
    };
  }

  setSortModel(model: GridSortModel, details: GridCallbackDetails): void {
    // Keep the built-in client sort (super) and separately notify the analytics observer.
    super.setSortModel(model, details);
    this.props.onSortTracked?.(model);
  }

  componentDidUpdate(prevProps: AttributeTableOwnProps<T>, prevState: AttributeTableState): void {
    super.componentDidUpdate(prevProps, prevState);
    if (prevProps.showProficiency !== this.props.showProficiency) {
      this.initColumns();
      this.forceUpdate();
    }
  }

  getRowId(row: T): string {
    return this.tableId + "_" + row.rowId;
  }

  onRowClick(params: GridRowParams<T>, e: SyntheticEvent): void {
    const { onAttributeSelect, rowHasSelect } = this.props;
    if (onAttributeSelect && (!rowHasSelect || rowHasSelect(params.row)))
      onAttributeSelect(e, params.row);
  }

  getTreeDataPath(row: T): string[] {
    return row.attributeHierarchy || [];
  }

  initColumns() {
    const {
      showProficiency,
      columns,
      sortable,
      readOnly,
      rowHasAction,
      rowNoActionIcon,
      onRequirementChange,
      childRowClass,
      rowActionDisabled,
      keyword
    } = this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === AttributeTable.columns.summary)
          this.columns.push({
            field: "summary",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            filterable: false,
            flex: 1,
            numRows: showProficiency ? 3 : 2,
            disableReorder: true,
            renderCell: (params) => (
              <div
                className={
                  "klayo-table_multilinerow" +
                  (this.props.tree && params.row.isChild === true ? " klayo-table_childattr" : "") +
                  (childRowClass ? " " + childRowClass(params.row) : "") +
                  (params.row.isParent ? " klayo-table_parentattr" : "") +
                  (params.row.isParent && (params.rowNode as TreeNodeWithChildren).childrenExpanded
                    ? " klayo-table_rowexpanded"
                    : "")
                }
              >
                <div className='klayo-attrtable_multilinerow_row1'>
                  {params.row.name || params.row.attribute}
                  {!this.props.tree && params.row.isParent ? " (parent)" : ""}
                </div>
                <div className='klayo-attrtable_multilinerow_row2'>
                  {params.row.type
                    ? (this.props.translate?.(params.row.type) ?? params.row.type)
                    : "Priority: n/a"}
                </div>
                {showProficiency && (
                  <div className='klayo-attrtable_multilinerow_row3'>
                    Proficiency:{" "}
                    {Utils.formatProficiencyFraction(
                      params.row.currentProficiency,
                      params.row.requiredProficiency,
                      params.row.validated
                    )}
                  </div>
                )}
              </div>
            )
          });

        if (c.type === AttributeTable.columns.summaryWithPeople)
          this.columns.push({
            field: "summaryWithPeople",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            filterable: false,
            flex: 1,
            numRows: showProficiency ? 3 : 2,
            disableReorder: true,
            renderCell: (params) => (
              <div className='klayo-table_multilinerow'>
                <div className='klayo-attrtable_multilinerow_row1'>
                  {params.row.name || params.row.attribute}
                </div>
                {showProficiency && (
                  <div className='klayo-attrtable_multilinerow_row2'>
                    Average proficiency: {Utils.formatProficiency(params.row.averageProficiency)}
                  </div>
                )}
                <div
                  className='klayo-attrtable_multilinerow_row3'
                  style={{ display: "flex", color: KLAYO_COLORS.GreyGR500 }}
                >
                  {params.row.employees?.length} people, {params.row.gapEmployees?.length} gaps
                </div>
              </div>
            )
          });

        if (c.type === AttributeTable.columns.lastUpdate)
          this.columns.push({
            field: "lastUpdate",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 200,
            filterable: false,
            disableReorder: true,
            renderCell: (params) => {
              if (
                this.props.highlightRow &&
                params.row.employeeAttributeId === this.props.highlightRow
              ) {
                this.onHighlightRow(params);
              }
              return <DateLabel date={params.row.lastUpdate} />;
            }
          });

        if (c.type === AttributeTable.columns.attribute)
          this.columns.push({
            field: "attribute",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            flex: 1,
            filterable: false,
            disableReorder: true,
            renderCell: (params) => {
              const rowNode = params.rowNode as TreeNodeWithChildren;
              const isExpanded = rowNode.childrenExpanded;
              const paddingLeft = `${rowNode.depth * 24}px`;
              return this.props.tree && params.row.isChild === true ? (
                <div
                  className={
                    "klayo-table_attrrow klayo-table_childattr" +
                    (childRowClass ? " " + childRowClass(params.row) : "")
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      paddingLeft: paddingLeft
                    }}
                  >
                    <span>
                      {highlightKeyword(
                        params.row.name ?? params.row.attribute ?? "",
                        keyword ?? ""
                      )}
                    </span>
                    {c.showJobMatchingIcon &&
                    params.row.isMinimum &&
                    this.props.organization?.enableJobMatching ? (
                      <StarIcon />
                    ) : null}
                  </div>
                </div>
              ) : (
                <div
                  className={
                    "klayo-table_attrrow" +
                    (params.row.isParent ? " klayo-table_parentattr" : "") +
                    (isExpanded ? " klayo-table_rowexpanded" : "")
                  }
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      paddingLeft
                    }}
                  >
                    <span>
                      {highlightKeyword(
                        params.row.name ?? params.row.attribute ?? "",
                        keyword ?? ""
                      )}
                      {!this.props.tree && params.row.isParent ? " (parent)" : ""}
                    </span>
                    {c.showJobMatchingIcon &&
                    params.row.isMinimum &&
                    this.props.organization?.enableJobMatching ? (
                      <StarIcon />
                    ) : null}
                  </div>
                </div>
              );
            },
            valueGetter: (_value, row) => {
              return row.name ?? row.attribute;
            }
          });

        if (c.type === AttributeTable.columns.requestTitle)
          this.columns.push({
            field: "requestTitle",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            flex: 1,
            filterable: false,
            disableReorder: true,
            renderCell: (params) => {
              const translationKey = params.row.validationRequestState?.translationKey;
              const statusLabel =
                translationKey && this.props.translate
                  ? this.props.translate(translationKey)
                  : params.row.statusLabel;
              return (
                <div className='klayo-table_requesttitle'>
                  <div className='klayo-table_requesttitle_attr'>{params.row.name}</div>
                  <div className='klayo-table_requesttitle_reason'>
                    {statusLabel}
                    {!params.row.hasRequiredDetails &&
                      params.row.status !== AbstractAttribute.status.review && (
                        <span className='klayo-table_requesttitle_requiresdata'>
                          {this.props.translate?.("status.requiresData") ?? "Requires data"}
                        </span>
                      )}
                  </div>
                </div>
              );
            },
            valueGetter: (_value, row) => row.name
          });

        if (c.type === AttributeTable.columns.category)
          this.columns.push({
            field: "category",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 220,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) =>
              !isEmpty(row.categories)
                ? row.categories?.map((cat) => (cat.name ? cat.name : cat)).join(", ")
                : "n/a"
          });

        if (c.type === AttributeTable.columns.attributeType)
          this.columns.push({
            field: "type",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 150,
            filterable: false,
            disableReorder: true,
            valueGetter: (_value, row) =>
              row.type ? (this.props.translate?.(row.type) ?? row.type) : "n/a"
          });

        if (c.type === AttributeTable.columns.requirementType)
          this.columns.push({
            field: "type",
            sortable: sortable,
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: c.selectable ? 250 : 200,
            filterable: false,
            disableReorder: true,
            renderCell: (params) =>
              (!params.row.isChild && c.selectable) ||
              (params.row.isChild && c.selectableOnChild) ? (
                params.row.isParent !== true ? (
                  <SelectMenu<any>
                    className='klayo-table_select'
                    clearable={false}
                    dense={true}
                    noSelectionError={true}
                    placeholder={this.props.translate?.("common.select") || "Select"}
                    value={params.row.requirementType}
                    items={Object.entries(AbstractAttribute.type)}
                    getItemLabel={(item) =>
                      item.translationKey
                        ? (this.props.translate?.(item.translationKey) ?? item.label)
                        : item.label
                    }
                    onChange={(e) => {
                      if (onRequirementChange) onRequirementChange(params.row, e.target.value);
                      this.forceUpdate();
                    }}
                    sx={{ width: "100%" }}
                  />
                ) : (
                  "n/a"
                )
              ) : (
                (params.row.requirementType as ReactNode)
              )
          });

        if (showProficiency) {
          if (c.type === AttributeTable.columns.averageProficiency)
            this.columns.push({
              field: "averageProficiency",
              headerName: getHeaderName(
                c,
                this.props.translate,
                this.props.translate?.("tableColumns.averageProficiency") || "Avg. proficiency"
              ),
              visibleFromBreakpoint: getVisibleFromBreakpoint(c),
              sortable: sortable,
              type: "number",
              width: 170,
              filterable: false,
              disableReorder: true,
              renderCell: (params) => Utils.formatProficiency(params.row.averageProficiency),
              sortComparator: (v1: number | null | undefined, v2: number | null | undefined) =>
                (v2 ?? 0) - (v1 ?? 0)
            });

          if (c.type === AttributeTable.columns.currentProficiency)
            this.columns.push({
              field: "currentProficiency",
              headerName: getHeaderName(
                c,
                this.props.translate,
                this.props.translate?.("tableColumns.currentProficiency") || "Current proficiency"
              ),
              visibleFromBreakpoint: getVisibleFromBreakpoint(c),
              sortable: sortable,
              type: "number",
              width: 120,
              filterable: false,
              disableReorder: true,
              renderCell: (params) =>
                Utils.formatProficiency(params.row.currentProficiency, params.row.validated),
              sortComparator: (v1: number | null | undefined, v2: number | null | undefined) =>
                (v2 ?? 0) - (v1 ?? 0),
              renderHeader: (params: { colDef: { headerName?: string } }) => (
                <div className='klayo-table_headerwrap'>{params.colDef.headerName}</div>
              )
            });

          if (c.type === AttributeTable.columns.requiredProficiency)
            this.columns.push({
              field: "requiredProficiency",
              headerName: getHeaderName(
                c,
                this.props.translate,
                this.props.translate?.("tableColumns.requiredProficiency") || "Required proficiency"
              ),
              visibleFromBreakpoint: getVisibleFromBreakpoint(c),
              sortable: sortable,
              type: "number",
              width: 120,
              filterable: false,
              disableReorder: true,
              renderCell: (params) =>
                Utils.formatProficiency(params.row.requiredProficiency, params.row.validated),
              sortComparator: (v1: number | null | undefined, v2: number | null | undefined) =>
                (v2 ?? 0) - (v1 ?? 0),
              renderHeader: (params: { colDef: { headerName?: string } }) => (
                <div className='klayo-table_headerwrap'>{params.colDef.headerName}</div>
              )
            });
        }

        if (c.type === AttributeTable.columns.status)
          this.columns.push({
            field: "statusLabel",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 190,
            filterable: false,
            sortable: sortable,
            disableReorder: true,
            disableColumnMenu: true,
            disableColumnFilter: true,
            renderCell: (params) => {
              if (
                params.row.validationRequestType ===
                  EmployeeAttributeValidationRequestType.ExpiredAttribute &&
                params.row.validationRequestState?.index ===
                  Abstract_Attribute.status.feedback.index
              )
                return this.props.translate?.("status.expired");

              const translationKey = params.row.validationRequestState?.translationKey;
              if (translationKey && this.props.translate) {
                return this.props.translate(translationKey);
              }
              return params.row.statusLabel;
            }
          });

        if (c.type === AttributeTable.columns.reason)
          this.columns.push({
            field: "reasonLabel",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 190,
            filterable: false,
            sortable: sortable,
            disableReorder: true,
            valueGetter: (_value, row) => {
              return row.hasValidationRequest?.()
                ? row.validationRequestState?.label
                : row.reasonLabel;
            },
            renderCell: (params) => {
              const state = params.row.hasValidationRequest?.()
                ? params.row.validationRequestState
                : params.row.reason;
              const translationKey = state?.translationKey;
              if (translationKey && this.props.translate) {
                return this.props.translate(translationKey);
              }
              return params.row.hasValidationRequest?.()
                ? params.row.validationRequestState?.label
                : params.row.reasonLabel;
            }
          });

        if (c.type === AttributeTable.columns.validationRequestStateTest)
          this.columns.push({
            field: "validationRequestStateTest",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 200,
            filterable: false,
            sortable: sortable,
            disableReorder: true,
            renderCell: (params) => {
              return (
                <div>
                  <div>
                    Old:{" "}
                    {params.row.originalValidationRequestState
                      ? params.row.originalValidationRequestState?.longLabel
                      : ""}
                  </div>
                  <div>
                    New:{" "}
                    {params.row.hasValidationRequest?.()
                      ? params.row.validationRequestState?.longLabel
                      : params.row.reasonLabel}
                  </div>
                </div>
              );
            }
          });

        if (c.type === AttributeTable.columns.expiryTest)
          this.columns.push({
            field: "expiryTest",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 200,
            filterable: false,
            sortable: sortable,
            disableReorder: true,
            renderCell: (params) => {
              return (
                <div>
                  <div>
                    Old:{" "}
                    {params.row.expiryDate ? Utils.formatReadableDate(params.row.expiryDate) : "-"}
                  </div>
                  <div>
                    New:{" "}
                    {params.row.requestedExpiryDate
                      ? Utils.formatReadableDate(params.row.requestedExpiryDate)
                      : "-"}
                  </div>
                </div>
              );
            }
          });

        if (c.type === AttributeTable.columns.reasonLong)
          this.columns.push({
            field: "reasonLabelLong",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 250,
            align: "right",
            headerAlign: "right",
            filterable: false,
            sortable: sortable,
            disableReorder: true,
            valueGetter: (_value, row) => {
              return row.hasValidationRequest?.() && !row.isParent
                ? row.validationRequestState?.longLabel
                : row.reasonLabel;
            },
            renderCell: (params) => {
              if (params.row.hasValidationRequest?.() && !params.row.isParent) {
                const longLabelKey = params.row.validationRequestState?.longLabelTranslationKey;
                if (longLabelKey && this.props.translate) {
                  return this.props.translate(longLabelKey);
                }
                return params.row.validationRequestState?.longLabel;
              }
              return params.row.reasonLabel;
            }
          });

        if (c.type === AttributeTable.columns.comment)
          this.columns.push({
            field: "comment",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 120,
            filterable: false,
            sortable: sortable,
            disableReorder: true
          });

        if (c.type === AttributeTable.columns.documents)
          this.columns.push({
            field: "documents",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 90,
            filterable: false,
            type: "number",
            sortable: sortable,
            disableReorder: true,
            valueGetter: (_value, row) => (row.files ? Object.entries(row.files).length : 0)
          });

        if (c.type === AttributeTable.columns.expiry)
          this.columns.push({
            field: "expiry",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 90,
            filterable: false,
            sortable: sortable,
            disableReorder: true,
            valueGetter: (_value, row) =>
              row.expiryDate ? Utils.formatReadableDate(row.expiryDate) : 0
          });

        if (c.type === AttributeTable.columns.hasTrainingCourses)
          this.columns.push({
            field: "hasTrainingCourses",
            headerName: getHeaderName(c, this.props.translate),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            filterable: false,
            align: "right",
            width: 36,
            disableReorder: true,
            renderCell: (params) =>
              params.row.hasTrainingCourses ? (
                <StyledAttributeCertificationCell>
                  <TooltipMUI
                    classes={{ tooltip: "klayo__attribute-certification-tooltip" }}
                    title={
                      <div>
                        {this.props.translate?.("tooltips.trainingMapped") ?? "Training mapped"}
                      </div>
                    }
                    placement='left'
                    arrow
                  >
                    <CertificateIcon className='certification-icon' />
                  </TooltipMUI>
                </StyledAttributeCertificationCell>
              ) : null
          });

        if (c.type === AttributeTable.columns.actions && !readOnly)
          this.columns.push({
            field: "actions",
            type: "actions",
            filterable: false,
            align: "right",
            width: 50,
            disableReorder: true,
            getActions: (params) => [
              rowHasAction === true || (rowHasAction && rowHasAction(params.row)) ? (
                <GridActionsCellItem
                  icon={<MoreVertIcon />}
                  label='Actions'
                  onClick={(e: any) => this.props.onAttributeAction?.(e, params.row)}
                />
              ) : rowNoActionIcon ? (
                <Tooltip
                  title={this.props.translate?.("status.underReview") ?? "Under review"}
                  placement='right'
                >
                  {rowNoActionIcon}
                </Tooltip>
              ) : (
                <div></div>
              )
            ]
          });

        if (c.type === AttributeTable.columns.actionsMore && !readOnly) {
          this.columns.push({
            field: "actions",
            type: "actions",
            filterable: false,
            align: "right",
            width: 50,
            disableReorder: true,

            getActions: (params) => [
              <GridActionsCellItem
                icon={<MoreVertIcon />}
                label='Actions'
                onClick={(e: any) => {
                  this.props.onAttributeAction?.(e, params.row);
                }}
                key={Math.random().toString(36).substring(7)}
              />
            ]
          });
        }

        if (c.type === AttributeTable.columns.delete && !readOnly)
          this.columns.push({
            field: "delete",
            type: "actions",
            filterable: false,
            align: "right",
            width: 50,
            disableReorder: true,
            /*getActions: params => [rowHasAction === true || (rowHasAction && rowHasAction(params.row)) ? (params.row.isChild === true ? <div></div> :
                        <GridActionsCellItem
                            className='klayo-table_deleteaction'
                            icon={<Tooltip title='Cannot be deleted as this attribute is part of a parent attribute' placement='left-start'><DeleteIcon /></Tooltip>}
                            label="Delete"
                            disabled={rowActionDisabled && rowActionDisabled(params.row)}
                            onClick={e => this.props.onAttributeDelete(e, params.row)}
                        />
                    ) : rowNoActionIcon ? rowNoActionIcon : (<div></div>)],*/
            renderCell: (params) =>
              rowHasAction === true || (rowHasAction && rowHasAction(params.row)) ? (
                params.row.isChild === true ? (
                  <div></div>
                ) : (
                  <Tooltip
                    title={
                      rowActionDisabled && rowActionDisabled(params.row)
                        ? (this.props.translate?.("tooltips.cannotDeleteCompetencyParent") ??
                          "Cannot be deleted as this competency is part of a parent competency")
                        : (this.props.translate?.("tooltips.delete") ?? "Delete")
                    }
                    placement='right'
                  >
                    <span>
                      <Button
                        icon={<DeleteIcon />}
                        disabled={rowActionDisabled && rowActionDisabled(params.row)}
                        onClick={(e: SyntheticEvent) =>
                          this.props.onAttributeDelete?.(e, params.row)
                        }
                      />
                    </span>
                  </Tooltip>
                )
              ) : rowNoActionIcon ? (
                rowNoActionIcon
              ) : (
                <div></div>
              )
          });

        if (c.type === AttributeTable.columns.edit)
          this.columns.push({
            field: "edit",
            filterable: false,
            align: "right",
            disableReorder: true,
            renderCell: (params) =>
              rowHasAction === true || (rowHasAction && rowHasAction(params.row)) ? (
                <Link
                  className='klayo-table_editlink'
                  onClick={(e) => this.props.onAttributeEdit?.(e, params.row)}
                >
                  Edit
                </Link>
              ) : (
                <div></div>
              )
          });

        if (c.type === AttributeTable.columns.gapIconOld)
          this.columns.push({
            field: "icon",
            headerName: "",
            width: 60,
            valueGetter: (_value, row) => {
              const gapReason = row.gapReason;
              return gapReason === AbstractAttribute.gapReason.notSet
                ? null
                : gapReason === AbstractAttribute.gapReason.met
                  ? 1
                  : 0;
            },
            renderCell: (params) => {
              const gapReason = params.row.gapReason;
              return gapReason === AbstractAttribute.gapReason.notSet ? null : gapReason ===
                AbstractAttribute.gapReason.met ? (
                <Tooltip title='gfgf' placement='right'>
                  <CheckIcon className='klayo-attrtable_checkicon' />
                </Tooltip>
              ) : (
                <Tooltip title='gfgf' placement='right'>
                  <WarningIcon className='klayo-attrtable_warningicon' />
                </Tooltip>
              );
            }
          });

        if (c.type === AttributeTable.columns.gapIcon)
          this.columns.push({
            field: "gapIconTest",
            headerName: "",
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 60,
            renderCell: (params) =>
              params.row.missing === false ? (
                <Tooltip
                  title={
                    this.props.translate?.("status.approvedCompetency") ?? "Approved competency"
                  }
                  placement='left'
                >
                  <CheckIcon className='klayo-attrtable_checkicon' />
                </Tooltip>
              ) : (
                <Tooltip
                  title={this.props.translate?.("status.missingCompetency") ?? "Missing competency"}
                  placement='left'
                >
                  <WarningIcon className='klayo-attrtable_warningicon' />
                </Tooltip>
              )
          });

        if (c.type === AttributeTable.columns.attachmentIcon)
          this.columns.push({
            field: "attachmentIcon",
            headerName: "",
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            width: 44,
            minWidth: 44,
            renderCell: (params) =>
              params.row.hasDocuments ||
              (params.row.files && Object.entries(params.row.files).length !== 0) ? (
                <AttachmentIcon className='klayo-attrtable_attachment' />
              ) : null
          });

        if (c.type === AttributeTable.columns.gapEmployees)
          this.columns.push({
            field: "gaps",
            headerName: getHeaderName(
              c,
              this.props.translate,
              this.props.translate?.("tableColumns.gaps") || "Gaps"
            ),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            width: 100,
            filterable: false,
            disableReorder: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) =>
              params.row.gapEmployees?.length ? (
                <Chip
                  label={params.row.gapEmployees?.length}
                  onClick={(e: any) => this.props.onSelectGap?.(e, params.row)}
                  textColor={KLAYO_COLORS.Red600}
                  backgroundColor={KLAYO_COLORS.Rose050}
                />
              ) : (
                0
              ),
            valueGetter: (_value, row) => {
              return row.gapEmployees?.length;
            }
          });
        if (c.type === AttributeTable.columns.metEmployees)
          this.columns.push({
            field: "met",
            headerName: getHeaderName(
              c,
              this.props.translate,
              this.props.translate?.("tableColumns.have") || "Have"
            ),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            width: 100,
            filterable: false,
            disableReorder: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) =>
              params.row.metEmployees?.length ? (
                <Chip
                  label={params.row.metEmployees?.length}
                  onClick={(e: any) => this.props.onSelectHave?.(e, params.row)}
                  textColor={KLAYO_COLORS.NeonBlueNB800}
                  backgroundColor={KLAYO_COLORS.NeonBlueNB050}
                />
              ) : (
                0
              ),
            valueGetter: (_value, row) => {
              return row.metEmployees?.length;
            }
          });

        if (c.type === AttributeTable.columns.attributeEmployees)
          this.columns.push({
            field: "people",
            headerName: getHeaderName(
              c,
              this.props.translate,
              this.props.translate?.("tableColumns.people") || "People"
            ),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            width: 100,
            filterable: false,
            disableReorder: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) =>
              params.row?.employees?.length ? (
                <Chip
                  label={params.row?.employees?.length}
                  onClick={(e: SyntheticEvent) => this.props.onSelectPeople?.(e, params.row)}
                  textColor={KLAYO_COLORS.GreyGR900}
                  backgroundColor={KLAYO_COLORS.GrayGR200}
                />
              ) : (
                0
              ),
            valueGetter: (_value, row) => {
              return row?.employees?.length || 0;
            }
          });

        if (c.type === AttributeTable.columns.employees)
          this.columns.push({
            field: "people",
            headerName: getHeaderName(
              c,
              this.props.translate,
              this.props.translate?.("tableColumns.people") || "People"
            ),
            sortable: sortable,
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            width: 200,
            filterable: false,
            disableReorder: true,
            align: "right",
            headerAlign: "right",
            renderCell: (params) => (
              <ProfilePictureGroup
                max={5}
                users={params.row.employees}
                onUserClick={(e: SyntheticEvent, u: unknown) => this.onUserClick(e, u, params.row)}
              />
            ),
            valueGetter: (_value, row) => {
              return row?.employees?.length ?? 0;
            }
          });

        if (c.type === AttributeTable.columns.numJobs)
          this.columns.push({
            field: c.type.field,
            headerName: getHeaderName(
              c,
              this.props.translate,
              this.props.translate?.("tableColumns.jobs") || "Jobs"
            ),
            type: "number",
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            sortable: sortable,
            width: 110,
            filterable: false,
            disableReorder: true,
            align: "right",
            valueGetter: (_value, row) => row.numberOfJobAttributes,
            renderCell: (params) => (
              <Chip
                label={params.row.numberOfJobAttributes}
                textColor={KLAYO_COLORS.GreyGR900}
                onClick={(e: SyntheticEvent) => {
                  e.stopPropagation();
                  this.props.onJobSelect?.(e, params.row);
                }}
                backgroundColor={KLAYO_COLORS.GrayGR200}
              />
            )
          });

        if (c.type === AttributeTable.columns.numEmployees)
          this.columns.push({
            field: c.type.field,
            type: "number",
            headerName: getHeaderName(
              c,
              this.props.translate,
              this.props.translate?.("tableColumns.employees") || "Employees"
            ),
            visibleFromBreakpoint: getVisibleFromBreakpoint(c),
            visibleToBreakpoint: getVisibleToBreakpoint(c),
            sortable: sortable,
            width: 110,
            filterable: false,
            disableReorder: true,
            align: "right",
            valueGetter: (_value, row) => row.numberOfEmployeeAttributes
          });

        if (c.type === AttributeTable.columns.needsFeedback)
          this.columns.push({
            field: c.type.field,
            type: "number",
            headerName: getHeaderName(c, this.props.translate, ""),
            sortable: sortable,
            align: "right",
            width: 50,
            filterable: false,
            disableReorder: true,
            renderCell: (params) =>
              params.row.needsFeedback === true && !params.row.isChild ? (
                <TooltipIcon
                  icon={<NeedsFeedbackIcon />}
                  text={
                    params.row.comment
                      ? params.row.comment
                      : (this.props.translate?.("tooltips.competencyNeedsFeedback") ??
                        "This competency needs feedback")
                  }
                  placement='bottom'
                />
              ) : (
                ""
              )
          });
      }
    });
  }
}

const StyledAttributeCertificationCell = styled.div`
  display: flex;
  justify-content: center;
  .certification-icon {
    width: 20px;
    height: 20px;
    /* padding: 16px 8px; */
    color: ${KLAYO_COLORS.GreyGR400};

    :hover {
      color: ${KLAYO_COLORS.NeonBlueNB200};
      cursor: pointer;
    }
  }
`;
